import type { GetStaticProps } from "next"
import type { MDXRemoteSerializeResult } from "next-mdx-remote"
import { memo } from "react"
import { Disclaimer } from "@/components/resources/components/Disclaimer"
import { ArticleHero } from "@/components/resources/components/Hero"
import { LoadingIndicatorBox } from "@/components/resources/components/LoadingIndicatorBox"
import { MarkdownContentRenderer } from "@/components/resources/components/MarkdownContentRenderer"
import { ArticleSchema } from "@/components/resources/components/Schemas"
import { PageMeta } from "@/components/resources/components/Seo/PageMeta"
import { TableOfContentsLayout } from "@/components/resources/components/TableOfContents"
import { NewsletterTeaser } from "@/components/resources/components/TeaserGrid/NewsletterTeaser"
import { ScrollOffsetProvider } from "@/components/resources/context/ScrollPage/ScrollPageContext"
import {
  getPost,
  getPostSlugs,
  type MarkdownPost,
} from "@/components/resources/data/markdown/posts"
import type { PostProps } from "@/components/resources/data/sanity/posts"
import { getSinglePost } from "@/components/resources/data/sanity/posts"
import { SiteLayout } from "@/components/resources/layouts/site"
import { ArticlePage } from "@/components/resources/templates/ArticlePage"

/**
 * Markdown-based engineering post props
 */
interface MarkdownPostProps extends MarkdownPost {
  source: "markdown"
}

/**
 * Sanity CMS engineering post props
 */
interface SanityPostProps extends PostProps {
  source: "sanity"
}

type EngineeringPostPageProps = MarkdownPostProps | SanityPostProps

const EngineeringArticleContent = memo(
  ({ mdxSource }: { mdxSource: MDXRemoteSerializeResult }) => (
    <>
      <div className="max-w-3xl mx-auto px-4">
        <MarkdownContentRenderer source={mdxSource} />
        <Disclaimer />
      </div>
      <div className="flex justify-center mb-8 md:mb-12 lg:mb-16 pt-8 md:pt-12 lg:pt-16 max-w-5xl mx-auto px-4">
        <NewsletterTeaser as="section" />
      </div>
    </>
  ),
  () => true,
)

EngineeringArticleContent.displayName = "EngineeringArticleContent"

const MarkdownArticle = ({ post, mdxSource, meta }: MarkdownPostProps) => {
  return (
    <>
      <PageMeta meta={meta} />
      <article className="mb-8 md:mb-12 lg:mb-16">
        <div className="max-w-7xl mx-auto px-4">
          <ArticleHero
            categoryName={post.categoryName}
            category={post.category}
            title={post.title}
            subtitle={post.subtitle}
            image={post.image || undefined}
            publishedAt={post.publishedAt}
            author={post.author}
            projectMeta={post.projectMeta}
            releaseNumber={post.releaseNumber}
          />
        </div>
        {post.tableOfContents ? (
          <ScrollOffsetProvider scrollOffset={100}>
            <TableOfContentsLayout tableOfContents={post.tableOfContents}>
              <EngineeringArticleContent mdxSource={mdxSource} />
            </TableOfContentsLayout>
          </ScrollOffsetProvider>
        ) : (
          <EngineeringArticleContent mdxSource={mdxSource} />
        )}
        <ArticleSchema article={post} />
      </article>
    </>
  )
}

const SinglePost = (props: EngineeringPostPageProps) => {
  if (props.source === "sanity") {
    if (!props.post) {
      return <LoadingIndicatorBox />
    }
    return (
      <>
        <PageMeta meta={props.meta} />
        <ArticlePage {...props.post} />
      </>
    )
  }

  return <MarkdownArticle {...props} />
}

SinglePost.getLayout = SiteLayout

export default SinglePost

export async function getStaticPaths() {
  const slugs = getPostSlugs("engineering")

  return {
    paths: slugs.map((slug) => ({ params: { slug } })),
    fallback: "blocking",
  }
}

export const getStaticProps: GetStaticProps<EngineeringPostPageProps, { slug?: string }> = async ({
  params,
}) => {
  const { slug } = params ?? {}

  if (!slug) {
    throw new Error("Slug cannot be empty, check getStaticPaths")
  }

  // Try markdown first
  const post = await getPost("engineering", slug)

  if (post) {
    return {
      props: { source: "markdown", ...post },
      revalidate: 180,
    }
  }

  // Fall back to Sanity CMS for older engineering posts
  const sanityProps = await getSinglePost({ category: "engineering", slug })

  if (sanityProps) {
    return {
      props: {
        source: "sanity",
        ...sanityProps,
      },
      revalidate: 180,
    }
  }

  return {
    notFound: true,
  }
}
